import * as Sentry from '@sentry/react-native'
import { QueryClientProvider } from '@tanstack/react-query'
import { useFonts } from 'expo-font'
import { Stack } from 'expo-router'
import { StatusBar } from 'expo-status-bar'
import { useEffect } from 'react'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { MD3DarkTheme, MD3LightTheme, PaperProvider } from 'react-native-paper'
import { useAppUpdates } from '@/hooks/useAppUpdates'
import { useColorScheme } from '@/hooks/useColorScheme'
import { useRealtimeHealthCheck } from '@/hooks/useRealtimeHealthCheck'
import { useSyncActiveLocations } from '@/hooks/useSyncActiveLocations'
import { AuthProvider } from '@/lib/auth'
import { ImpersonationProvider } from '@/lib/impersonation'
import {
    requestNotificationPermissions,
    setupNotificationResponseListener,
} from '@/lib/notifications'
import { NoticeRenderer } from '@/ui/NoticeRenderer'

import 'react-native-reanimated'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { PBTSDBProvider, queryClient } from '@/lib/pocketbase'

export { ErrorBoundary } from '@/ui/ErrorBoundary'

function AuthDependentHooks() {
    useRealtimeHealthCheck()
    return null
}

const customLightTheme = {
    ...MD3LightTheme,
    custom: {
        placeholder: '#bdbdbd',
    },
}

const customDarkTheme = {
    ...MD3DarkTheme,
    colors: {
        ...MD3DarkTheme.colors,
        background: '#121212',
        surface: '#1e1e1e',
        surfaceVariant: '#2c2c2c',
    },
    custom: {
        placeholder: '#aaaaaa',
    },
}

export default Sentry.wrap(function RootLayout() {
    const colorScheme = useColorScheme()
    const [loaded] = useFonts({
        SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
        MaterialDesignIcons: require('@react-native-vector-icons/material-design-icons/fonts/MaterialDesignIcons.ttf'),
    })

    useAppUpdates()
    useSyncActiveLocations()

    useEffect(() => {
        requestNotificationPermissions()
        const cleanup = setupNotificationResponseListener()
        return cleanup
    }, [])

    if (!loaded) {
        return null
    }

    const paperTheme = colorScheme === 'dark' ? customDarkTheme : customLightTheme

    return (
        <GestureHandlerRootView style={{ flex: 1 }}>
            <SafeAreaProvider>
                <QueryClientProvider client={queryClient}>
                    <PBTSDBProvider>
                        <AuthProvider>
                            <AuthDependentHooks />
                            <ImpersonationProvider>
                                <PaperProvider theme={paperTheme}>
                                    <NoticeRenderer />
                                    <Stack>
                                        <Stack.Screen
                                            name="index"
                                            options={{ headerShown: false }}
                                        />
                                        <Stack.Screen
                                            name="[orgSlug]"
                                            options={{
                                                headerShown: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="auth"
                                            options={{ headerShown: false }}
                                        />
                                        <Stack.Screen name="+not-found" />
                                    </Stack>
                                    <StatusBar style="auto" />
                                </PaperProvider>
                            </ImpersonationProvider>
                        </AuthProvider>
                    </PBTSDBProvider>
                </QueryClientProvider>
            </SafeAreaProvider>
        </GestureHandlerRootView>
    )
})
